import { DegreeCardDetailed } from '@highereducation/hen-ui';
import { type FC, useEffect, useRef, useMemo, useCallback } from 'react';
import cn from '../../shared/utils/classNames';
import { uuid } from '../../shared/utils';
import useFlowTrackingEvents from '../../hooks/useFlowTrackingEvents';
import { useIntersect } from '../../hooks/useIntersect';
import { createProductViewedPayload } from '../../utils/createEventingPayloadsDE';
import { EventingLocation } from '../../types/enums';
import type { CardClickCorrelation, Result } from '../../types/result';
import tagularEvent from '../../utils/tagularEvent';
import { createResultsEventContext } from '../../utils/createResultsEventContext';
import { resolveActiveProductFlags } from '../ProductFlags/productFlagsResolve';
import { useComparisonForProgram } from '../DegreeCardDetailed/useComparisonForProgram';
import { resolveApplicationCtaHref } from '../../utils/resolveApplicationCtaHref';
import { ctaUrlForTelemetry } from '../../utils/sanitizeOutboundUrlForTelemetry';
import { useAppViewConversion } from '../../hooks/useAppViewConversion';
import {
  paragraphToSentencesArray,
  tokenizeKeywords,
} from '../../utils/programDescriptions';
import PROGRAM_KEYWORDS from './ProgramDescription/keywords';

interface CardProps {
  position?: number;
  eventingOverrides?: { [key: string]: any };
  index?: number;
  onProductClick?: (correlation: CardClickCorrelation) => void;
  onNextStepsClick?: (correlation: CardClickCorrelation) => void;
  importantFactors?: string[];
  imageURL?: string;
  isProgramSubmitted?: boolean;
  showComparisonCheckbox?: boolean;
  applicationCtaLabel?: string;
  applicationCtaURL?: string;
  /**
   * Click-time interpolation config for `applicationCtaURL` (EDUN-14483).
   * Absent when the resolved CTA carries no map, so the card's raw
   * `window.open(applicationCtaURL)` behavior is unchanged.
   */
  applicationCtaFieldMap?: Record<string, string>;
  applicationCtaStaticParams?: Record<string, string>;
  applicationCtaMissingValue?: 'empty' | 'omit' | 'hide';
  /**
   * Lead / partner-identifier sources for the Apply CTA interpolator
   * (EDUN-14483). `partnerIdentifiers` comes off the LDS v4 submit
   * response; both are only meaningful once the card's program has
   * already been submitted (`isProgramSubmitted`), matching when this
   * card actually opens `applicationCtaURL`.
   */
  lead?: object;
  partnerIdentifiers?: Record<string, string>;
  /**
   * Renders the compact recommender layout (image-only header, no
   * border-t, tighter padding). Used when the card is rendered inside a
   * recommender popup or rail.
   */
  isRecommender?: boolean;
  schoolName: string;
  schoolId?: string;
  schoolSlug: string;
  programName: string;
  city: string;
  state: string;
  programId?: number;
  capId?: number;
  /**
   * Offering identity forwarded so the submitted-card "start your application"
   * `window.open` can create the CMS "APP_VIEWED" conversion at click time
   * (Supersonic parity). `offeringId` is the CMS v2 id;`offeringUuid` is the
   * forward `/match` identity used as a fallback.
   */
  offeringId?: string;
  offeringUuid?: string;
  organizationId?: string;
  audience?: string;
  /**
   * School TCPA HTML from marketing/org lookup. Carried into compare
   * selection so the comparison modal can render Terms and Conditions
   * without a second fetch on the legacy Results path.
   */
  tcpa?: string;
  snippet: string;
  levelOfInstitution?: string;
  accreditator?: string;
  roiPercentileRank?: number;
  percentOnlineEnrollment?: number;
  isOnline?: boolean;
  isPublic?: boolean;
  forProfit?: boolean;
  notForProfit?: boolean;
  location?: EventingLocation;
  showPopupExperience?: boolean;
  degreeSlug?: string;
  categorySlug?: string;
  subjectSlug?: string;
  listId?: string;
  isUserRequested?: boolean;
}

/**
 * Single unified Card component. Replaces the legacy
 * `Card/variants/{UnderGradCard,GradCard}.tsx` pair. Uses hen-ui
 * `DegreeCardDetailed` for layout; eventing semantics (EMBEDDED_RECOMMENDER
 * vs PAGE) match the prior custom card.
 */
const Card: FC<CardProps> = ({
  isRecommender = false,
  schoolName,
  schoolId,
  schoolSlug,
  programName,
  city,
  state,
  programId,
  capId,
  snippet,
  levelOfInstitution,
  accreditator,
  roiPercentileRank,
  percentOnlineEnrollment,
  isOnline,
  isPublic,
  forProfit,
  notForProfit,
  onProductClick,
  onNextStepsClick,
  isProgramSubmitted = false,
  applicationCtaLabel,
  applicationCtaURL,
  applicationCtaFieldMap,
  applicationCtaStaticParams,
  applicationCtaMissingValue,
  lead,
  partnerIdentifiers,
  showComparisonCheckbox = false,
  location = EventingLocation.PAGE,
  audience,
  degreeSlug,
  categorySlug,
  subjectSlug,
  position,
  listId,
  isUserRequested = true,
  showPopupExperience = false,
  offeringId,
  offeringUuid,
  organizationId,
  tcpa = '',
}) => {
  const { createAppViewConversion } = useAppViewConversion();
  const visibilityRef = useRef<HTMLDivElement>(null);
  const hasViewedRef = useRef(false);
  const {
    triggerProductLoadedEvent,
    triggerProductClickedEvent,
    eventContext,
  } = useFlowTrackingEvents();

  const isEmbeddedRecommender =
    location === EventingLocation.EMBEDDED_RECOMMENDER;

  // Interpolated Apply href (EDUN-14483): a pass-through to
  // `applicationCtaURL` when no fieldMap/staticParams are configured, so
  // this is a no-op for the common case.
  const resolvedApplicationCtaUrl = resolveApplicationCtaHref(
    {
      applicationCtaURL,
      applicationCtaFieldMap,
      applicationCtaStaticParams,
      applicationCtaMissingValue,
    },
    { lead, partnerIdentifiers },
  );

  // Per-instance correlation ids: useRef guarantees stability for the
  // card instance's lifetime (unlike useMemo, which React may evict), so
  // ProductLoaded (mount) / ProductViewed (intersect) / ProductClicked
  // (later) all share one view id, and the click → conversion path ties
  // to one click id. Each rendered card instance mints its own, so the
  // same offering on a different surface gets a distinct correlation.
  const instanceViewCorrelationId = useRef(uuid()).current;
  const instanceProductClickedCorrelationId = useRef(uuid()).current;

  const programPresets = {
    degree: degreeSlug || null,
    category: categorySlug || null,
    subject: subjectSlug || null,
  };

  const { setNodes, entries } = useIntersect();
  const isInView = entries[0]?.isIntersecting ?? false;

  useEffect(() => {
    if (visibilityRef.current) {
      setNodes([visibilityRef.current]);
    }
  }, [setNodes]);

  const resultEventContext = createResultsEventContext({
    eventContext,
    listId,
    presets: programPresets,
    location,
    productClickedCorrelationId: instanceProductClickedCorrelationId,
    viewCorrelationId: instanceViewCorrelationId,
    brand: schoolSlug,
    position,
    capId,
    programId,
    outboundUrl: applicationCtaURL,
    isProgramSubmitted,
    isUserRequested,
  });

  // Embedded-recommender rails suppress view events while the popup is
  // open (the popup itself fires its own events). All other locations
  // fire view events as soon as the card scrolls into view.
  useEffect(() => {
    if (hasViewedRef.current) return;

    const shouldFireViewEvent = isEmbeddedRecommender
      ? isInView && !showPopupExperience
      : isInView;

    if (shouldFireViewEvent) {
      const payload = createProductViewedPayload({
        ...resultEventContext,
      });
      tagularEvent(payload.name, payload.payload as any);
      hasViewedRef.current = true;
      setNodes([]);
    }
    // Intentionally mirrors legacy Card: fire once when visibility flips; do not
    // re-subscribe on every context identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parity with prior Card view effect
  }, [isInView, showPopupExperience]);

  const resultData: Result = useMemo(
    () => ({
      schoolName,
      schoolId: schoolId ?? '',
      schoolSlug,
      city,
      state,
      programId: programId!,
      capId,
      programName,
      snippet,
      levelOfInstitution: levelOfInstitution || '',
      accreditator: accreditator || '',
      percentOnlineEnrollment: percentOnlineEnrollment || null,
      roiPercentileRank,
      isOnline: isOnline || false,
      isPublic,
      forProfit,
      notForProfit: notForProfit || false,
      applicationCtaLabel: applicationCtaLabel || '',
      applicationCtaURL: applicationCtaURL || '',
      programFlow: [],
      tcpa,
      productClickedCorrelationId: instanceProductClickedCorrelationId,
      offeringId,
      offeringUuid,
      organizationId,
      audience,
    }),
    [
      schoolName,
      schoolId,
      schoolSlug,
      city,
      state,
      programId,
      capId,
      programName,
      snippet,
      levelOfInstitution,
      accreditator,
      percentOnlineEnrollment,
      roiPercentileRank,
      isOnline,
      isPublic,
      forProfit,
      notForProfit,
      applicationCtaLabel,
      applicationCtaURL,
      instanceProductClickedCorrelationId,
      offeringId,
      offeringUuid,
      organizationId,
      audience,
      tcpa,
    ],
  );

  useEffect(() => {
    triggerProductLoadedEvent({
      ...resultEventContext,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only product loaded (legacy)
  }, []);

  const handleCardClick = useCallback(() => {
    triggerProductClickedEvent({
      ...resultEventContext,
      outboundUrl: isProgramSubmitted
        ? ctaUrlForTelemetry(resolvedApplicationCtaUrl, applicationCtaFieldMap)
        : applicationCtaURL,
    });

    const clickCorrelation: CardClickCorrelation = {
      viewCorrelationId: instanceViewCorrelationId,
      productClickedCorrelationId: instanceProductClickedCorrelationId,
    };

    if (isProgramSubmitted && resolvedApplicationCtaUrl) {
      // Routing into the external application view — create the CMS
      // "APP_VIEWED" conversion at this click (Supersonic parity), deduped
      // once per selection, before opening the application.
      createAppViewConversion(resultData);
      window.open(resolvedApplicationCtaUrl, '_blank', 'noopener,noreferrer');
    } else if (
      isProgramSubmitted &&
      !resolvedApplicationCtaUrl &&
      onNextStepsClick
    ) {
      // No usable Apply URL — either none was configured, or interpolation
      // hid it (`missingValue: 'hide'`) — fall back to the "Next Steps"
      // drawer instead of a dead click.
      onNextStepsClick(clickCorrelation);
    } else if (onProductClick) {
      onProductClick(clickCorrelation);
    }
  }, [
    triggerProductClickedEvent,
    resultEventContext,
    isProgramSubmitted,
    resolvedApplicationCtaUrl,
    applicationCtaURL,
    onNextStepsClick,
    onProductClick,
    createAppViewConversion,
    resultData,
    instanceViewCorrelationId,
    instanceProductClickedCorrelationId,
    applicationCtaFieldMap,
  ]);

  const submittedButtonLabel = resolvedApplicationCtaUrl
    ? applicationCtaLabel || 'Start Your Application'
    : 'Next Steps';

  const ctaLabel = !isProgramSubmitted
    ? 'Explore Program'
    : isRecommender
      ? 'Submitted'
      : submittedButtonLabel;

  const schoolLogoUrl = `https://simple-storage-server.highereducation.com/${schoolSlug}.png?w=130&h=80`;
  const schoolDetailsLine =
    [city, state]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(', ') || undefined;

  const tags = useMemo(
    () =>
      resolveActiveProductFlags({
        levelOfInstitution,
        accreditator,
        roiPercentileRank,
        percentOnlineEnrollment,
        isOnline,
        isPublic,
        forProfit,
        notForProfit,
      }).map(({ flag }) => flag.value),
    [
      levelOfInstitution,
      accreditator,
      roiPercentileRank,
      percentOnlineEnrollment,
      isOnline,
      isPublic,
      forProfit,
      notForProfit,
    ],
  );

  // Compare-checkbox props (checked / disabled / onChange + analytics) are
  // derived from the shared ComparisonProvider via the useComparisonForProgram
  // hook. The hook returns `compare: false` when no provider is mounted, so
  // we gate visibility on `showComparisonCheckbox && programId` AND the
  // hook's own provider-presence check.
  const programCompareProps = useComparisonForProgram(resultData, {
    position,
    viewCorrelationId: resultEventContext.viewCorrelationId,
    correlationId: resultEventContext.productClickedCorrelationId,
  });

  const whyConsiderItems = useMemo(() => {
    if (!snippet) return [];

    return paragraphToSentencesArray(snippet)
      .map((sentence) => sentence.trim())
      .filter(Boolean)
      .map((sentence, index) => {
        const segments = tokenizeKeywords(sentence, PROGRAM_KEYWORDS);
        const content = segments.map((seg, i) =>
          seg.bold ? <b key={i}>{seg.text}</b> : seg.text,
        );
        return <span key={`${index}-${sentence.slice(0, 24)}`}>{content}</span>;
      });
  }, [snippet]);

  const showCompare = Boolean(
    showComparisonCheckbox && programId && programCompareProps.compare,
  );
  const stackFooterUntilSm = showCompare;

  /** Mirrors whole-card click while skipping hen-ui read-more and compare controls. */
  const handleCardSurfaceClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const t = e.target as HTMLElement;
      // Footer (compare + CTA): label clicks bubble on real CheckboxButton; do not treat as card click.
      if (t.closest('[data-degree-card-footer]')) return;
      if (t.closest('button[aria-controls]')) return;
      handleCardClick();
    },
    [handleCardClick],
  );

  const handleCtaClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      e.stopPropagation();
      handleCardClick();
    },
    [handleCardClick],
  );

  return (
    <DegreeCardDetailed
      ref={visibilityRef}
      border='stroke'
      readMoreBehavior='expand'
      schoolLogo={schoolLogoUrl}
      schoolName={schoolName}
      schoolDetails={schoolDetailsLine}
      degreeTitle={programName}
      tags={tags.length > 0 ? tags : undefined}
      description={snippet}
      listHeading={
        whyConsiderItems.length > 0 ? 'Why Consider This Program' : undefined
      }
      listItems={whyConsiderItems.length > 0 ? whyConsiderItems : undefined}
      ctaLabel={ctaLabel}
      onCtaClick={handleCtaClick}
      compare={showCompare}
      compareChecked={showCompare ? programCompareProps.compareChecked : false}
      compareDisabled={
        showCompare ? programCompareProps.compareDisabled : false
      }
      onCompareChange={
        showCompare ? programCompareProps.onCompareChange : undefined
      }
      footerLayout={stackFooterUntilSm ? 'stackUntilSm' : 'row'}
      className={cn(
        'not-prose h-full w-full min-w-0 max-w-full min-h-[376px] cursor-pointer',
        isRecommender
          ? 'rounded-lg border-t-8 pt-3'
          : 'rounded-sm border-t-4 pt-3',
      )}
      onClick={handleCardSurfaceClick}
    />
  );
};

export default Card;
